/**
 * The scan flow, given an engine: recognise each captured side, run the three
 * parsers over what came out, merge them, validate, check the category, and map
 * to the form prefill. Pure apart from the engine it is handed, so it is tested
 * against a stub that returns fixtures (run-scan.test) — no camera, no WASM.
 *
 * OPS-1: one run can be called off (its signal), and each side has a time
 * limit; either way the engine is told to stop, and the dialog that started
 * the run is the only one that reads its answer.
 */

import { type MergedScan, mergeScans, type ScanPrefill, toPrefill } from './map-to-identity'
import {
  type DocumentScanEngine,
  type OcrLine,
  ScanCancelled,
  type ScanImage,
  type ScannedIdentity,
} from './ocr-interface'
import { parseAamva } from './parsers/aamva-pdf417'
import { parseEuLicence } from './parsers/eu-licence'
import { type MrzResult, parseMrz } from './parsers/mrz'
import { type CategoryCheck, checkCategory, type LicenceRule } from './scan-config'
import { type ValidationIssue, validate } from './validate'

export interface ScanInput {
  /** One or more captured sides (licence front/back, an ID card, a passport page). */
  readonly images: readonly ScanImage[]
  /** ISO date the vehicle is due back; the licence must still be valid then. */
  readonly returnDate?: string
  /** The booked category's licence rule, for the (warn-only) category check. */
  readonly licence?: LicenceRule
}

export interface ScanOptions {
  /** Fires to call the run off: the engine stops and ScanCancelled is thrown. */
  readonly signal?: AbortSignal
  /** Per side; past it ScanTimeout is thrown and the engine stops. */
  readonly timeoutMs?: number
}

export interface ScanOutcome {
  /** What to pour into the hand-over form (operator confirms every field). */
  readonly prefill: ScanPrefill
  /** Flags for the operator; none of them block. */
  readonly issues: readonly ValidationIssue[]
  /** 'mismatch' surfaces a warning; 'ok'/'unknown' do not (Decision 5). */
  readonly category: CategoryCheck
  /** The full merged read, for the review UI's confidence highlighting. */
  readonly identity: ScannedIdentity
}

/** A side that took longer than the limit to read (a huge or blurred photo). */
export class ScanTimeout extends Error {
  constructor() {
    super('the scan took too long')
    this.name = 'ScanTimeout'
  }
}

/** 25 s per side: Tesseract on an iPad reads a 1,600 px document in a few seconds. */
export const SCAN_TIMEOUT_MS = 25_000

/** Both engines read one side, stopped together by the caller's signal or the time limit. */
async function readSide(
  engine: DocumentScanEngine,
  image: ScanImage,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<{ lines: readonly OcrLine[]; barcode: string | null }> {
  const stop = new AbortController()
  let timedOut = false
  const onAbort = () => stop.abort()
  if (signal?.aborted) throw new ScanCancelled()
  signal?.addEventListener('abort', onAbort, { once: true })
  const timer = setTimeout(() => {
    timedOut = true
    stop.abort()
  }, timeoutMs)
  try {
    const [ocr, barcode] = await Promise.all([
      engine.recognizeText(image, { signal: stop.signal }),
      engine.readBarcode(image, { signal: stop.signal }).catch((error: unknown) => {
        if (error instanceof ScanCancelled) throw error
        return null
      }),
    ])
    return { lines: ocr.lines, barcode }
  } catch (error) {
    if (timedOut) throw new ScanTimeout()
    throw error
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  }
}

export async function runScan(
  engine: DocumentScanEngine,
  input: ScanInput,
  { signal, timeoutMs = SCAN_TIMEOUT_MS }: ScanOptions = {},
): Promise<ScanOutcome> {
  // Recognise text on every side, and try the barcode on each (null when none).
  const lines: OcrLine[] = []
  const barcodes: string[] = []
  for (const image of input.images) {
    const side = await readSide(engine, image, signal, timeoutMs)
    lines.push(...side.lines)
    if (side.barcode) barcodes.push(side.barcode)
  }
  if (signal?.aborted) throw new ScanCancelled()

  // Parse each source. MRZ and the EU front read all recognised lines; the AAMVA
  // parser reads each decoded barcode and we take the first that is really AAMVA.
  const mrz: MrzResult | null = parseMrz(lines)
  const euLicence = parseEuLicence(lines)
  const aamva = barcodes.map(parseAamva).find((r) => r !== null) ?? null

  const parts: ScannedIdentity[] = [euLicence]
  if (mrz) parts.push(mrz.identity)
  if (aamva) parts.push(aamva)

  const merged: MergedScan = mergeScans(...parts)
  const issues = validate(merged.identity, {
    returnDate: input.returnDate,
    mrz,
    licenceCountry: merged.identity.issuingCountry?.value,
    names: merged.names,
  })
  const category = checkCategory(merged.identity.licenceCategories?.value, input.licence)

  return { prefill: toPrefill(merged.identity), issues, category, identity: merged.identity }
}
